/**
 * What a screen shows for one request: its data, its failure, or that it is
 * still coming.
 *
 * Written once because the shortcut everywhere was `if (q.isLoading || !q.data)
 * return <LoadingState />` — which, when the request fails, is a spinner for
 * ever: the reader cannot tell waiting from broken, and is never offered a
 * retry. Data first, so a page that has its answer keeps showing it when a
 * background refresh fails.
 */

import type { ReactNode } from 'react';

import { ApiError } from './api-client';

export interface QueryLike<T> {
  data: T | undefined;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
}

export type QueryView<T> =
  | { kind: 'data'; data: T }
  | { kind: 'error'; message: string; requestId?: string; retry: () => void }
  | { kind: 'loading' };

export function queryView<T>(query: QueryLike<T>, fallback = 'This could not be loaded.'): QueryView<T> {
  if (query.data !== undefined) return { kind: 'data', data: query.data };
  if (query.isError) {
    const error = query.error;
    return {
      kind: 'error',
      message: error instanceof ApiError ? error.message : fallback,
      requestId: error instanceof ApiError && error.requestId ? error.requestId : undefined,
      retry: () => void query.refetch(),
    };
  }
  return { kind: 'loading' };
}

/**
 * One figure from one request — a tile's number, a total: `show(data)` once it
 * is here, `failed` (a dash) when it could not be read, `pending` while it
 * comes. Never a 0 for an answer nobody read: a failed count drawn as 0 reads
 * as "nothing open", and a failed sum as "₹0 owed".
 */
export function queryValue<T>(
  query: QueryLike<T>,
  show: (data: T) => ReactNode,
  pending: ReactNode,
  failed: ReactNode = '—',
): ReactNode {
  const view = queryView(query);
  if (view.kind === 'data') return show(view.data);
  return view.kind === 'error' ? failed : pending;
}

/**
 * A select fed by a request — its placeholder, and the error under it when the
 * request failed. `ready(data)` is the placeholder once the options are here
 * ("Choose a template", or "No template is ready" when the list is empty);
 * while they come it is "Loading…"; when they could not be read it says so,
 * rather than claiming there is nothing to choose.
 */
export function optionsState<T>(
  query: QueryLike<T>,
  ready: (data: T) => string,
  noun: string,
): { placeholder: string; error?: string } {
  const view = queryView(query);
  if (view.kind === 'data') return { placeholder: ready(view.data) };
  if (view.kind === 'loading') return { placeholder: 'Loading…' };
  return { placeholder: '—', error: `The ${noun} could not be read: ${view.message}` };
}
